import React, { useMemo, useRef, useState } from 'react';

import {
  PAYMENT_MODES,
  PAYMENT_MODE_DEFAULT,
  isDepositCollectable,
  ADMISSION_STATUS_LABELS,
} from '../../../constants/admission';
import { formatPaise } from '../../../utils/money';
import { newIdempotencyKey } from '../../../services/api/admissionPaymentsApi';
import { describeBalance } from './billModel';

const overlayCls = 'fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4';
const panelCls = 'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl w-full max-w-md max-h-[92vh] overflow-y-auto';
const btnPrimary = 'px-4 py-2 rounded-lg bg-teal-600 text-white text-sm font-semibold disabled:opacity-60';
const btnSecondary = 'px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 text-sm';
const inputCls = 'w-full px-2.5 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 text-sm';
const labelCls = 'block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1';

const modeChipOn = 'px-3 py-1.5 rounded-lg text-[12.5px] font-semibold border bg-teal-600 border-teal-600 text-white';
const modeChipOff = 'px-3 py-1.5 rounded-lg text-[12.5px] font-semibold border bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-800 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-700';

/**
 * TakeDepositDialog (C4 · S1) — collect an advance deposit against an admission.
 * ONE component mounted on BOTH surfaces (running-bill header action + Admission
 * Desk row action); the admission is pre-bound, never a picker.
 *
 * The SERVER prices/validates and stamps received-by + department; the client
 * "balance after" preview is explicitly labelled server-computed. Save is
 * disabled-until-valid and single-flight (busy guard) with a fresh
 * X-Idempotency-Key per open → a double-click posts ONE deposit (idempotent
 * replay). A server rejection keeps the dialog open with values retained + an
 * inline error. In-app modal only — no window.confirm.
 *
 * On success it shows a receipt hand-off (own IP receipt no. + deposit-to-date +
 * balance-after) with a Print button that opens the PDF; closing triggers the
 * parent's bounded reload.
 *
 * C5 (additive, discharge hand-off): optional `prefillAmountPaise` seeds the
 * amount field (e.g. the exact settle-to-zero amount) and optional
 * `expectedBalancePaise` rides on the submitted body as `expected_balance_paise`
 * (contract §10 settle-intent — a race loses to 409 BALANCE_CHANGED). Both are
 * no-ops when omitted — the plain C4 deposit flow is unaffected.
 *
 * @param onSubmit(body) → { success, data?, error?, code? } (calls collectDeposit)
 * @param onPrintReceipt(paymentId, {isRefund}) → opens the receipt PDF
 */
export default function TakeDepositDialog({ admission, balancePaise, depositsHeldPaise, onClose, onSubmit, onPrintReceipt, prefillAmountPaise, expectedBalancePaise }) {
  const idemKey = useRef(newIdempotencyKey('deposit'));
  const collectable = isDepositCollectable(admission.status);

  const [amount, setAmount] = useState(prefillAmountPaise > 0 ? String(prefillAmountPaise / 100) : ''); // rupees, as typed
  const [method, setMethod] = useState(PAYMENT_MODE_DEFAULT);
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null); // success envelope

  const modeMeta = PAYMENT_MODES.find((m) => m.value === method) || PAYMENT_MODES[0];
  const needsReference = !!modeMeta.reference;

  const amountNum = Number(amount);
  const amountPaise = Math.round((Number.isFinite(amountNum) ? amountNum : 0) * 100);
  const amountValid = Number.isFinite(amountNum) && amountPaise > 0;
  const referenceValid = !needsReference || reference.trim().length > 0;
  const valid = collectable && amountValid && referenceValid;

  // Suggested chip = the amount currently owed (only when the patient owes).
  const owedPaise = Math.max(0, Math.round(Number(balancePaise) || 0));
  const showSuggest = owedPaise > 0;

  // Preview only — server recomputes authoritatively. balance_after = owed − deposit.
  const afterPreview = useMemo(() => {
    if (!amountValid) return null;
    return describeBalance((Math.round(Number(balancePaise) || 0)) - amountPaise);
  }, [amountValid, amountPaise, balancePaise]);

  const pickMode = (m) => {
    setMethod(m);
    const meta = PAYMENT_MODES.find((x) => x.value === m);
    if (!meta || !meta.reference) setReference(''); // switching to cash → clear the reference
  };

  const fillOwed = () => setAmount(String(owedPaise / 100));

  const submit = async () => {
    if (!valid || busy) return;
    setBusy(true);
    setError(null);
    const res = await onSubmit({
      amount_paise: amountPaise,
      method,
      reference_number: needsReference ? reference.trim() : undefined,
      notes: note.trim() || undefined,
      idempotency_key: idemKey.current,
      ...(expectedBalancePaise !== undefined ? { expected_balance_paise: expectedBalancePaise } : {}),
    });
    setBusy(false);
    if (res && res.success) {
      setDone(res.data);
    } else if (res) {
      setError(
        res.code === 'INVALID_METHOD' ? 'That payment mode isn’t enabled for your organisation.'
          : res.code === 'REFERENCE_REQUIRED' ? 'A reference number is required for this mode.'
            : res.code === 'ADMISSION_NOT_ACTIVE' ? 'Deposits can only be collected on an admitted patient.'
              : res.error || 'Could not collect the deposit. Check the values and try again.',
      );
      // values retained — dialog stays open
    }
  };

  const ctxBalance = describeBalance(balancePaise);

  // ── Success: receipt hand-off ────────────────────────────────────────────
  if (done) {
    const payment = done.payment || {};
    const afterBal = describeBalance(done.balance_paise_after);
    return (
      <div className={overlayCls} data-testid="take-deposit-dialog">
        <div className={panelCls}>
          <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 flex items-center">
            <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Deposit collected</h3>
            <button type="button" onClick={onClose} className="ml-auto text-slate-500 dark:text-slate-400" aria-label="Close">✕</button>
          </div>
          <div className="p-4 space-y-3" data-testid="take-deposit-success">
            <div className="rounded-lg border border-teal-300 dark:border-teal-700 bg-teal-50 dark:bg-teal-900/20 text-teal-800 dark:text-teal-200 text-[13px] p-3">
              Receipt <b className="tabular-nums">{payment.receipt_number || '—'}</b> is ready.
            </div>
            <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 px-3 py-2.5 text-[13px] space-y-1">
              <div className="flex justify-between"><span className="text-slate-500 dark:text-slate-400">Deposit received</span><b className="tabular-nums text-slate-900 dark:text-white">{formatPaise(payment.amount_paise)}</b></div>
              <div className="flex justify-between"><span className="text-slate-500 dark:text-slate-400">Deposits to date (net)</span><b className="tabular-nums text-slate-900 dark:text-white">{formatPaise(done.deposit_total_paise_after)}</b></div>
              <div className="flex justify-between"><span className="text-slate-500 dark:text-slate-400">{afterBal.label} after this deposit</span><b className="tabular-nums text-slate-900 dark:text-white">{afterBal.text}</b></div>
            </div>
            <p className="text-[11px] text-slate-500 dark:text-slate-400">Advance deposit against the running bill — this is not a tax invoice. No GST has been charged on this bill.</p>
          </div>
          <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-2">
            <button type="button" className={btnSecondary} onClick={onClose} data-testid="take-deposit-done">Done</button>
            {onPrintReceipt && payment.payment_id && (
              <button type="button" className={btnPrimary} onClick={() => onPrintReceipt(payment.payment_id, { isRefund: false })} data-testid="take-deposit-print">Print receipt</button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Collect form ─────────────────────────────────────────────────────────
  return (
    <div className={overlayCls} data-testid="take-deposit-dialog">
      <div className={panelCls}>
        <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 flex items-center">
          <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Add deposit</h3>
          <button type="button" onClick={onClose} className="ml-auto text-slate-500 dark:text-slate-400" aria-label="Close">✕</button>
        </div>
        <div className="p-4 space-y-3">
          <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-[12.5px] text-slate-600 dark:text-slate-300 px-3 py-2">
            <b className="text-slate-900 dark:text-white">{admission.patient_name || admission.patient_id}</b> · {admission.ip_number}
            {' · '}{ctxBalance.label} <b className="text-slate-900 dark:text-white">{ctxBalance.text}</b>
            {depositsHeldPaise != null && <> · Deposits held <b className="text-slate-900 dark:text-white">{formatPaise(depositsHeldPaise)}</b></>}
          </div>

          {!collectable ? (
            <div className="rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-200 text-[12.5px] p-2.5" data-testid="take-deposit-blocked">
              Deposits can’t be collected on this admission — it is <b>{ADMISSION_STATUS_LABELS[admission.status] || admission.status}</b>. To take an advance before a bed is ready, admit the patient without a bed.
            </div>
          ) : (
            <>
              {error && (
                <div className="rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 text-[12.5px] p-2.5" data-testid="take-deposit-error">{error}</div>
              )}

              <div>
                <label className={labelCls} htmlFor="td-amount">Amount (₹)</label>
                <div className="flex gap-2 items-center flex-wrap">
                  <input id="td-amount" className={`${inputCls} max-w-[180px]`} type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0" data-testid="take-deposit-amount" />
                  {showSuggest && (
                    <button type="button" onClick={fillOwed} className="px-2.5 py-1.5 rounded-lg text-[12px] font-semibold border border-teal-400 dark:border-teal-600 bg-teal-50 dark:bg-teal-900/30 text-teal-800 dark:text-teal-200 hover:bg-teal-100 dark:hover:bg-teal-900/50" data-testid="take-deposit-suggest">
                      Owed now · {formatPaise(owedPaise)}
                    </button>
                  )}
                </div>
              </div>

              <div>
                <label className={labelCls}>Mode</label>
                <div className="flex gap-1.5 flex-wrap" role="group" aria-label="Payment mode">
                  {PAYMENT_MODES.map((m) => (
                    <button
                      key={m.value}
                      type="button"
                      onClick={() => pickMode(m.value)}
                      className={method === m.value ? modeChipOn : modeChipOff}
                      data-testid={`take-deposit-mode-${m.value}`}
                    >{m.label}</button>
                  ))}
                </div>
                <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">Modes come from your organisation’s enabled payment modes — same as OPD.</p>
              </div>

              {needsReference && (
                <div data-testid="take-deposit-reference-field">
                  <label className={labelCls} htmlFor="td-ref">{modeMeta.refLabel || 'Reference no.'}</label>
                  <input id="td-ref" className={inputCls} type="text" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="e.g. 4128 5567 9012" data-testid="take-deposit-reference" />
                </div>
              )}

              <div className="flex gap-3 flex-wrap">
                <div className="flex-1 min-w-[120px]">
                  <label className={labelCls}>Received by</label>
                  <span className="inline-flex items-center text-[11px] font-semibold px-2 py-0.5 rounded-full border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300">You — stamped on save</span>
                </div>
                <div className="flex-1 min-w-[140px]">
                  <label className={labelCls} htmlFor="td-note">Note (optional)</label>
                  <input id="td-note" className={inputCls} type="text" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. advance for surgery" maxLength={200} />
                </div>
              </div>

              <div className="flex items-center justify-between rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 px-3 py-2.5">
                <span className="text-[13px] text-slate-600 dark:text-slate-300">{afterPreview ? afterPreview.label : 'Balance'} after this deposit</span>
                <b className="text-[15px] tabular-nums text-slate-900 dark:text-white" data-testid="take-deposit-after">{afterPreview ? afterPreview.text : '—'}</b>
              </div>
              <p className="text-[11px] text-slate-500 dark:text-slate-400">Preview only — the final balance is computed by the server when you save.</p>
            </>
          )}
        </div>
        <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-2">
          <button type="button" className={btnSecondary} onClick={onClose}>Cancel</button>
          <button type="button" className={btnPrimary} onClick={submit} disabled={!valid || busy} data-testid="take-deposit-confirm">
            {busy ? 'Collecting…' : 'Collect & print receipt'}
          </button>
        </div>
      </div>
    </div>
  );
}
